import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../rbac';
import { financeAPI } from '../../services/api';
import useBackButtonClose from '../../hooks/useBackButtonClose';

/* ─── Constants ─── */
const INCOME_CATS = [
  'property_tax', 'water_tax', 'service_fee', 'certificate_fee', 'license_fee',
  'rent', 'government_grant', 'finance_commission', 'mgnrega', 'state_scheme',
  'donation', 'interest', 'penalty', 'other_income',
];
const EXPENSE_CATS = [
  'salary', 'water_supply', 'electricity', 'road_maintenance', 'drainage',
  'sanitation', 'health', 'education', 'agriculture', 'administration',
  'office_supplies', 'vehicle_maintenance', 'infrastructure', 'construction',
  'contingency', 'loan_repayment', 'other_expense',
];
const BUDGET_CATS = [
  'water_supply_sanitation', 'roads_drainage', 'health_sanitation',
  'education', 'agriculture_livelihood', 'administration_staff',
  'infrastructure', 'contingency_emergency', 'other',
];
const PAYMENT_MODES = ['cash', 'online', 'bank_transfer', 'cheque', 'dd', 'upi'];
const BILL_TYPES = ['vendor_bill', 'contractor_bill', 'utility_bill', 'salary_bill', 'maintenance_bill', 'other'];
const FUND_SOURCES = ['central_government', 'state_government', 'district_council', 'panchayat_samiti', 'ngo', 'csr', 'self', 'other'];
const REPORT_TYPES = ['ledger', 'cash-book', 'bill-register', 'grant-register', 'budget-utilization', 'receipt-register'];

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('mr-IN');
const outstanding = (bill) => Math.max(0, Number(bill?.total_amount || 0) - Number(bill?.paid_amount || 0));
const MARATHI_LABELS = {
  property_tax: 'मालमत्ता कर', water_tax: 'पाणी कर', service_fee: 'सेवा शुल्क',
  certificate_fee: 'प्रमाणपत्र शुल्क', license_fee: 'परवाना शुल्क', rent: 'भाडे',
  government_grant: 'शासकीय अनुदान', finance_commission: 'वित्त आयोग', mgnrega: 'मनरेगा',
  state_scheme: 'राज्य योजना', donation: 'देणगी', interest: 'व्याज', penalty: 'दंड',
  other_income: 'इतर उत्पन्न',
  salary: 'वेतन', water_supply: 'पाणी पुरवठा', electricity: 'वीज',
  road_maintenance: 'रस्ता देखभाल', drainage: 'गटार', sanitation: 'स्वच्छता',
  health: 'आरोग्य', education: 'शिक्षण', agriculture: 'शेती', administration: 'प्रशासन',
  office_supplies: 'कार्यालय साहित्य', vehicle_maintenance: 'वाहन देखभाल',
  infrastructure: 'पायाभूत सुविधा', construction: 'बांधकाम',
  contingency: 'आकस्मिक खर्च', loan_repayment: 'कर्ज परतफेड',
  bill_payment: 'बिल देयक', other_expense: 'इतर खर्च',
  water_supply_sanitation: 'पाणी पुरवठा व स्वच्छता', roads_drainage: 'रस्ते व गटार',
  health_sanitation: 'आरोग्य व स्वच्छता', agriculture_livelihood: 'शेती व उपजीविका',
  administration_staff: 'प्रशासन व कर्मचारी', contingency_emergency: 'आकस्मिक व आणीबाणी',
  other: 'इतर',
  cash: 'रोख', online: 'ऑनलाइन', bank_transfer: 'बँक हस्तांतरण', cheque: 'चेक', dd: 'डीडी', upi: 'यूपीआय',
  vendor_bill: 'विक्रेता बिल', contractor_bill: 'ठेकेदार बिल',
  utility_bill: 'उपयोगिता बिल', salary_bill: 'वेतन बिल', maintenance_bill: 'देखभाल बिल',
  central_government: 'केंद्र शासन', state_government: 'राज्य शासन',
  district_council: 'जिल्हा परिषद', panchayat_samiti: 'पंचायत समिती',
  ngo: 'स्वयंसेवी संस्था', csr: 'सीएसआर', self: 'स्वनिधी',
  draft: 'मसुदा', proposed: 'प्रस्तावित', approved: 'मंजूर', active: 'सक्रिय', closed: 'बंद',
  pending: 'प्रलंबित', partially_paid: 'अंशतः देय', paid: 'पूर्ण देय', overdue: 'मुदत उलटलेली',
  cancelled: 'रद्द', sanctioned: 'मंजूर', partially_received: 'अंशतः प्राप्त',
  fully_received: 'पूर्ण प्राप्त', utilized: 'वापरलेला', refunded: 'परतावा', expired: 'कालबाह्य',
  income: 'उत्पन्न', expense: 'खर्च',
  transaction: 'व्यवहार', budget: 'अंदाजपत्रक', bill: 'बिल', fund: 'निधी',
};
const cap = (s) => MARATHI_LABELS[s] || (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const today = () => new Date().toISOString().split('T')[0];
const valueAt = (row, key) => key.split('.').reduce((obj, part) => obj?.[part], row);
const sortRows = (rows, sort) => [...rows].sort((a, b) => {
  const av = valueAt(a, sort.key);
  const bv = valueAt(b, sort.key);
  const result = typeof av === 'number' || typeof bv === 'number'
    ? Number(av || 0) - Number(bv || 0)
    : String(av ?? '').localeCompare(String(bv ?? ''), 'mr', { numeric: true, sensitivity: 'base' });
  return sort.direction === 'asc' ? result : -result;
});
const SortHeader = ({ sort, setSort, column, children }) => (
  <button
    type="button"
    className={`fin-sort-header ${sort.key === column ? 'active' : ''}`}
    onClick={() => setSort(current => ({
      key: column,
      direction: current.key === column && current.direction === 'asc' ? 'desc' : 'asc',
    }))}
  >
    {children}
    <span>{sort.key === column ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
  </button>
);

export default function Finance() {
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;
  const canCreate = hasPermission(user, 'finance', 'create');
  const canUpdate = hasPermission(user, 'finance', 'update');
  const canDelete = hasPermission(user, 'finance', 'delete');
  const canPay = hasPermission(user, 'finance', 'pay');
  const canApprove = hasPermission(user, 'finance', 'approve');
  const canExport = hasPermission(user, 'finance', 'export');

  const [tab, setTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Data
  const [summary, setSummary] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [txPagination, setTxPagination] = useState({});
  const [budgets, setBudgets] = useState([]);
  const [bills, setBills] = useState([]);
  const [billPagination, setBillPagination] = useState({});
  const [funds, setFunds] = useState([]);

  // Filters
  const [summaryYear, setSummaryYear] = useState('');
  const [txType, setTxType] = useState('');
  const [txSearch, setTxSearch] = useState('');
  const [txCategory, setTxCategory] = useState('');
  const [txStatus, setTxStatus] = useState('');
  const [txPaymentMode, setTxPaymentMode] = useState('');
  const [txFinancialYear, setTxFinancialYear] = useState('');
  const [txFromDate, setTxFromDate] = useState('');
  const [txToDate, setTxToDate] = useState('');
  const [txPage, setTxPage] = useState(1);
  const [budgetYear, setBudgetYear] = useState('');
  const [billStatus, setBillStatus] = useState('');
  const [billSearch, setBillSearch] = useState('');
  const [billPage, setBillPage] = useState(1);
  const [fundYear, setFundYear] = useState('');

  // Forms
  const [showForm, setShowForm] = useState(null); // 'income' | 'expense' | 'budget' | 'bill' | 'fund' | 'pay_bill' | null
  const [formData, setFormData] = useState({});
  const [editId, setEditId] = useState(null);

  useBackButtonClose(!!showForm, () => closeForm());

  const closeForm = () => { setShowForm(null); setFormData({}); setEditId(null); };
  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  /* ─── Data Loading ─── */
  const loadSummary = useCallback(async () => {
    if (!slug) return;
    try {
      const params = {};
      if (summaryYear) params.financial_year = summaryYear;
      const r = await financeAPI.getSummary(slug, params);
      setSummary(r.data.summary || {});
    } catch (err) {
      setError(err.response?.data?.error || 'आर्थिक सारांश लोड झाला नाही');
    }
  }, [slug, summaryYear]);

  const loadTransactions = useCallback(async () => {
    if (!slug) return;
    try {
      const params = { page: txPage, limit: 25 };
      if (txType) params.type = txType;
      if (txSearch) params.search = txSearch;
      if (txCategory) params.category = txCategory;
      if (txStatus) params.status = txStatus;
      if (txPaymentMode) params.payment_mode = txPaymentMode;
      if (txFinancialYear) params.financial_year = txFinancialYear;
      if (txFromDate) params.from_date = txFromDate;
      if (txToDate) params.to_date = txToDate;
      const r = await financeAPI.getTransactions(slug, params);
      setTransactions(r.data.transactions || []);
      setTxPagination(r.data.pagination || {});
    } catch (err) {
      setError(err.response?.data?.error || 'व्यवहार लोड झाले नाहीत');
    }
  }, [slug, txPage, txType, txSearch, txCategory, txStatus, txPaymentMode, txFinancialYear, txFromDate, txToDate]);

  const loadBudgets = useCallback(async () => {
    if (!slug) return;
    try {
      const params = {};
      if (budgetYear) params.financial_year = budgetYear;
      const r = await financeAPI.getBudgets(slug, params);
      setBudgets(r.data.budgets || []);
    } catch (err) { setError(err.response?.data?.error || 'अंदाजपत्रक लोड झाले नाही'); }
  }, [slug, budgetYear]);

  const loadBills = useCallback(async () => {
    if (!slug) return;
    try {
      const params = { page: billPage, limit: 25 };
      if (billStatus) params.status = billStatus;
      if (billSearch) params.search = billSearch;
      const r = await financeAPI.getBills(slug, params);
      setBills(r.data.bills || []);
      setBillPagination(r.data.pagination || {});
    } catch (err) { setError(err.response?.data?.error || 'बिले लोड झाली नाहीत'); }
  }, [slug, billPage, billStatus, billSearch]);

  const loadFunds = useCallback(async () => {
    if (!slug) return;
    try {
      const params = {};
      if (fundYear) params.financial_year = fundYear;
      const r = await financeAPI.getFunds(slug, params);
      setFunds(r.data.funds || []);
    } catch (err) { setError(err.response?.data?.error || 'निधी लोड झाला नाही'); }
  }, [slug, fundYear]);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    Promise.all([loadSummary(), loadTransactions(), loadBudgets(), loadBills(), loadFunds()])
      .finally(() => setLoading(false));
  }, [slug, loadSummary, loadTransactions, loadBudgets, loadBills, loadFunds]);

  /* ─── Form Submission ─── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (showForm === 'bill' && formData.bill_date && formData.due_date && formData.bill_date > formData.due_date) {
        setError('देय तारीख बिल तारखेपूर्वी असू शकत नाही.');
        return;
      }
      if (showForm === 'fund') {
        if (formData.sanction_date && formData.release_date && formData.sanction_date > formData.release_date) {
          setError('वितरण तारीख मंजुरी तारखेपूर्वी असू शकत नाही.');
          return;
        }
        const earliestDeadlineDate = formData.release_date || formData.sanction_date;
        if (earliestDeadlineDate && formData.utilization_deadline && earliestDeadlineDate > formData.utilization_deadline) {
          setError('वापराची अंतिम तारीख मंजुरी किंवा वितरण तारखेपूर्वी असू शकत नाही.');
          return;
        }
      }
      if (showForm === 'income' || showForm === 'expense') {
        const payload = { ...formData, type: showForm, transaction_date: formData.transaction_date || today() };
        if (editId) {
          await financeAPI.updateTransaction(slug, editId, payload);
          flash('व्यवहार अद्ययावत केला!');
        } else {
          await financeAPI.createTransaction(slug, payload);
          flash('व्यवहार नोंदवला!');
        }
        loadTransactions();
        loadSummary();
      } else if (showForm === 'budget') {
        if (editId) {
          await financeAPI.updateBudget(slug, editId, formData);
          flash('अंदाजपत्रक अद्ययावत केले!');
        } else {
          await financeAPI.createBudget(slug, formData);
          flash('अंदाजपत्रक तयार केले!');
        }
        loadBudgets();
        loadSummary();
      } else if (showForm === 'bill') {
        if (editId) {
          await financeAPI.updateBill(slug, editId, formData);
          flash('बिल अद्ययावत केले!');
        } else {
          await financeAPI.createBill(slug, formData);
          flash('बिल तयार केले!');
        }
        loadBills();
        loadSummary();
      } else if (showForm === 'pay_bill') {
        const paymentAmount = Number(formData.amount || 0);
        const maxAmount = Number(formData.max_amount || 0);
        if (maxAmount > 0 && paymentAmount > maxAmount) {
          setError(`Payment cannot exceed outstanding amount ${fmt(maxAmount)}`);
          return;
        }
        await financeAPI.payBill(slug, editId, formData);
        flash('देयक नोंदवले!');
        loadBills();
        loadTransactions();
        loadSummary();
      } else if (showForm === 'fund') {
        if (editId) {
          await financeAPI.updateFund(slug, editId, formData);
          flash('निधी अद्ययावत केला!');
        } else {
          await financeAPI.createFund(slug, formData);
          flash('निधी वाटप तयार केले!');
        }
        loadFunds();
        loadSummary();
      }
      closeForm();
    } catch (err) {
      setError(err.response?.data?.error || 'कार्य अयशस्वी');
    }
  };

  const handleDelete = async (entity, id) => {
    if (!window.confirm(`हे ${cap(entity)} हटवायचे का?`)) return;
    try {
      if (entity === 'transaction') { await financeAPI.deleteTransaction(slug, id); loadTransactions(); loadSummary(); }
      else if (entity === 'budget') { await financeAPI.deleteBudget(slug, id); loadBudgets(); }
      else if (entity === 'bill') { await financeAPI.deleteBill(slug, id); loadBills(); loadSummary(); }
      else if (entity === 'fund') { await financeAPI.deleteFund(slug, id); loadFunds(); loadSummary(); }
      flash(`${cap(entity)} हटवले!`);
    } catch (err) { setError(err.response?.data?.error || 'हटवणे अयशस्वी'); }
  };

  const handleApprove = async (entity, id) => {
    try {
      if (entity === 'transaction') {
        const txn = transactions.find(t => Number(t.id) === Number(id));
        const budget_override_reason = txn?.type === 'expense' && txn?.budget_id && canApprove
          ? window.prompt('Budget override reason, if this approval exceeds allocation', '') || ''
          : '';
        await financeAPI.approveTransaction(slug, id, { budget_override_reason });
        loadTransactions();
        loadSummary();
      } else if (entity === 'budget') {
        await financeAPI.approveBudget(slug, id, { status: 'approved' });
        loadBudgets();
        loadSummary();
      } else if (entity === 'bill') {
        await financeAPI.approveBill(slug, id);
        loadBills();
      }
      flash(`${cap(entity)} मंजूर केले!`);
    } catch (err) {
      setError(err.response?.data?.error || 'मंजुरी अयशस्वी');
    }
  };

  const handleReverse = async (id) => {
    const reason = window.prompt('Reason for reversal');
    if (!reason) return;
    try {
      await financeAPI.reverseTransaction(slug, id, { reason });
      loadTransactions();
      loadSummary();
      loadBudgets();
      loadBills();
      loadFunds();
      flash('Transaction reversed!');
    } catch (err) {
      setError(err.response?.data?.error || 'Reversal failed');
    }
  };

  const handleExport = async (type) => {
    try {
      const params = {};
      if (type) params.type = type;
      if (txCategory) params.category = txCategory;
      if (txStatus) params.status = txStatus;
      if (txPaymentMode) params.payment_mode = txPaymentMode;
      if (txFinancialYear) params.financial_year = txFinancialYear;
      if (txFromDate) params.from_date = txFromDate;
      if (txToDate) params.to_date = txToDate;
      const r = await financeAPI.exportTransactions(slug, params);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `finance-${type || 'transactions'}-${today()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Export failed');
    }
  };

  const handleReportExport = async (report) => {
    try {
      const params = {};
      if (summaryYear) params.financial_year = summaryYear;
      const r = await financeAPI.exportReport(slug, report, params);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `finance-${report}-${today()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Report export failed');
    }
  };

  const set = (k, v) => setFormData(prev => ({ ...prev, [k]: v }));

  /* ─── Helpers ─── */
  const budgetYears = [...new Set(budgets.map(b => b.financial_year))].sort();
  const fundYears = [...new Set(funds.map(f => f.financial_year))].sort();
  const totalAllocated = budgets.reduce((s, b) => s + Number(b.allocated_amount || 0), 0);
  const totalSpent = budgets.reduce((s, b) => s + Number(b.actual_spent || 0), 0);
  const totalFundSanctioned = funds.reduce((s, f) => s + Number(f.sanctioned_amount || 0), 0);
  const totalFundReceived = funds.reduce((s, f) => s + Number(f.received_amount || 0), 0);
  const totalFundUtilized = funds.reduce((s, f) => s + Number(f.utilized_amount || 0), 0);

  const TAB_LIST = [
    { key: 'summary', icon: '📊', label: 'सारांश' },
    { key: 'income', icon: '💚', label: 'उत्पन्न' },
    { key: 'expense', icon: '🔴', label: 'खर्च' },
    { key: 'budgets', icon: '📋', label: 'अंदाजपत्रक' },
    { key: 'bills', icon: '🧾', label: 'बिले' },
    { key: 'funds', icon: '🏦', label: 'निधी' },
  ];

  return (
    <>
      <style>{finCSS}</style>
      <div className="fin-page">
        {/* Header */}
        <div className="fin-header">
          <div className="fin-heading-block">
            <span className="fin-heading-icon" aria-hidden="true">₹</span>
            <div>
              <span className="fin-eyebrow">आर्थिक प्रशासन</span>
              <h1 className="fin-title">आर्थिक व्यवस्थापन</h1>
              <p className="fin-subtitle">उत्पन्न, खर्च, अंदाजपत्रक, बिले आणि निधी यांचे एकत्रित व्यवस्थापन</p>
            </div>
          </div>
          <div className="fin-header-context"><span>ग्रामपंचायत</span><strong>{tenant?.name || 'ग्रामपंचायत'}</strong></div>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {/* Tabs */}
        <div className="fin-tabs">
          {TAB_LIST.map(t => (
            <button key={t.key} className={`fin-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              <span className="fin-tab-icon">{t.icon}</span> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>आर्थिक डेटा लोड होत आहे...</div>
        ) : (
          <>
            {/* ═══ SUMMARY TAB ═══ */}
            {tab === 'summary' && (
              <SummaryTab
                summary={summary} budgets={budgets}
                summaryYear={summaryYear} setSummaryYear={setSummaryYear}
                years={[...new Set([...budgets.map(b => b.financial_year), ...funds.map(f => f.financial_year)])].filter(Boolean).sort()}
                canExport={canExport}
                reportTypes={REPORT_TYPES}
                onReportExport={handleReportExport}
              />
            )}

            {/* ═══ INCOME TAB ═══ */}
            {tab === 'income' && (
              <TransactionTab
                title="उत्पन्न नोंदी" type="income" colorClass="income"
                transactions={transactions.filter(t => txType === '' ? t.type === 'income' : true)}
                allTransactions={transactions} pagination={txPagination}
                txType={txType} setTxType={setTxType} txSearch={txSearch} setTxSearch={setTxSearch}
                txPage={txPage} setTxPage={setTxPage}
                canCreate={canCreate} canUpdate={canUpdate} canDelete={canDelete} canApprove={canApprove} canExport={canExport}
                showForm={showForm} formData={formData} editId={editId}
                onAdd={() => { setShowForm('income'); setEditId(null); setFormData({ category: INCOME_CATS[0], transaction_date: today(), payment_mode: 'cash' }); }}
                onEdit={(t) => { setShowForm('income'); setEditId(t.id); setFormData({ ...t }); }}
                onDelete={(id) => handleDelete('transaction', id)}
                onApprove={(id) => handleApprove('transaction', id)}
                onReverse={handleReverse}
                onExport={() => handleExport('income')}
                onSubmit={handleSubmit} set={set} closeForm={closeForm}
                categories={INCOME_CATS} budgets={budgets} filterType="income"
                filters={{ txSearch, txCategory, txStatus, txPaymentMode, txFinancialYear, txFromDate, txToDate }}
                setters={{ setTxSearch, setTxCategory, setTxStatus, setTxPaymentMode, setTxFinancialYear, setTxFromDate, setTxToDate, setTxPage }}
              />
            )}

            {/* ═══ EXPENSE TAB ═══ */}
            {tab === 'expense' && (
              <TransactionTab
                title="खर्च नोंदी" type="expense" colorClass="expense"
                transactions={transactions.filter(t => txType === '' ? t.type === 'expense' : true)}
                allTransactions={transactions} pagination={txPagination}
                txType={txType} setTxType={setTxType} txSearch={txSearch} setTxSearch={setTxSearch}
                txPage={txPage} setTxPage={setTxPage}
                canCreate={canCreate} canUpdate={canUpdate} canDelete={canDelete} canApprove={canApprove} canExport={canExport}
                showForm={showForm} formData={formData} editId={editId}
                onAdd={() => { setShowForm('expense'); setEditId(null); setFormData({ category: EXPENSE_CATS[0], transaction_date: today(), payment_mode: 'cash' }); }}
                onEdit={(t) => { setShowForm('expense'); setEditId(t.id); setFormData({ ...t }); }}
                onDelete={(id) => handleDelete('transaction', id)}
                onApprove={(id) => handleApprove('transaction', id)}
                onReverse={handleReverse}
                onExport={() => handleExport('expense')}
                onSubmit={handleSubmit} set={set} closeForm={closeForm}
                categories={EXPENSE_CATS} budgets={budgets} filterType="expense"
                filters={{ txSearch, txCategory, txStatus, txPaymentMode, txFinancialYear, txFromDate, txToDate }}
                setters={{ setTxSearch, setTxCategory, setTxStatus, setTxPaymentMode, setTxFinancialYear, setTxFromDate, setTxToDate, setTxPage }}
              />
            )}

            {/* ═══ BUDGETS TAB ═══ */}
            {tab === 'budgets' && (
              <BudgetTab
                budgets={budgets} budgetYears={budgetYears} budgetYear={budgetYear} setBudgetYear={setBudgetYear}
                totalAllocated={totalAllocated} totalSpent={totalSpent}
                canCreate={canCreate} canUpdate={canUpdate} canDelete={canDelete} canApprove={canApprove}
                showForm={showForm} formData={formData} editId={editId}
                onAdd={() => { setShowForm('budget'); setEditId(null); setFormData({ financial_year: '2025-26', category: BUDGET_CATS[0], allocated_amount: '', status: 'draft' }); }}
                onEdit={(b) => { setShowForm('budget'); setEditId(b.id); setFormData({ ...b }); }}
                onDelete={(id) => handleDelete('budget', id)}
                onApprove={(id) => handleApprove('budget', id)}
                onSubmit={handleSubmit} set={set} closeForm={closeForm}
              />
            )}

            {/* ═══ BILLS TAB ═══ */}
            {tab === 'bills' && (
              <BillTab
                bills={bills} pagination={billPagination}
                billStatus={billStatus} setBillStatus={setBillStatus}
                billSearch={billSearch} setBillSearch={setBillSearch}
                billPage={billPage} setBillPage={setBillPage}
                canCreate={canCreate} canUpdate={canUpdate} canDelete={canDelete} canPay={canPay} canApprove={canApprove}
                showForm={showForm} formData={formData} editId={editId}
                onAdd={() => { setShowForm('bill'); setEditId(null); setFormData({ bill_type: 'vendor_bill', expense_category: 'other_expense', bill_date: today(), amount: '', tax_amount: '0' }); }}
                onEdit={(b) => { setShowForm('bill'); setEditId(b.id); setFormData({ ...b }); }}
                onDelete={(id) => handleDelete('bill', id)}
                onApprove={(id) => handleApprove('bill', id)}
                onPay={(b) => {
                  const remaining = outstanding(b);
                  setShowForm('pay_bill');
                  setEditId(b.id);
                  setFormData({ amount: remaining, max_amount: remaining, payment_mode: 'bank_transfer', transaction_date: today() });
                }}
                onSubmit={handleSubmit} set={set} closeForm={closeForm}
                budgets={budgets}
              />
            )}

            {/* ═══ FUNDS TAB ═══ */}
            {tab === 'funds' && (
              <FundTab
                funds={funds} fundYears={fundYears} fundYear={fundYear} setFundYear={setFundYear}
                totalFundSanctioned={totalFundSanctioned}
                totalFundReceived={totalFundReceived}
                totalFundUtilized={totalFundUtilized}
                canCreate={canCreate} canUpdate={canUpdate} canDelete={canDelete}
                showForm={showForm} formData={formData} editId={editId}
                onAdd={() => { setShowForm('fund'); setEditId(null); setFormData({ source: 'state_government', financial_year: '2025-26', sanctioned_amount: '' }); }}
                onEdit={(f) => { setShowForm('fund'); setEditId(f.id); setFormData({ ...f }); }}
                onDelete={(id) => handleDelete('fund', id)}
                onSubmit={handleSubmit} set={set} closeForm={closeForm}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   SUMMARY TAB
   ═══════════════════════════════════════════════════════════ */
function SummaryTab({ summary, budgets, summaryYear, setSummaryYear, years, canExport, reportTypes, onReportExport }) {
  const incCat = (summary.categoryBreakdown || []).filter(c => c.type === 'income');
  const expCat = (summary.categoryBreakdown || []).filter(c => c.type === 'expense');
  const [selectedReport, setSelectedReport] = useState(reportTypes[0] || 'ledger');
  const expenseRate = Number(summary.totalIncome || 0) > 0
    ? Math.round((Number(summary.totalExpense || 0) / Number(summary.totalIncome)) * 100)
    : 0;
  const budgetRate = budgets.length
    ? Math.round(budgets.reduce((total, budget) => total + Number(budget.utilization_pct || 0), 0) / budgets.length)
    : 0;

  return (
    <div className="fin-section">
      <div className="fin-summary-toolbar">
        <div><span>दृश्य कालावधी</span><select value={summaryYear} onChange={e => setSummaryYear(e.target.value)}><option value="">सर्व आर्थिक वर्षे</option>{years.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
        {canExport && <div className="fin-report-control"><span>अहवाल निर्यात</span><select value={selectedReport} onChange={e => setSelectedReport(e.target.value)}>{reportTypes.map(report => <option key={report} value={report}>{report.replace(/-/g, ' ')}</option>)}</select><button type="button" className="btn btn-outline" onClick={() => onReportExport(selectedReport)}>CSV डाउनलोड</button></div>}
      </div>
      {/* KPI Cards */}
      <div className="fin-kpi-row">
        <div className="fin-kpi income"><div className="fin-kpi-icon">↗</div><div className="fin-kpi-body"><span className="fin-kpi-label">एकूण उत्पन्न</span><span className="fin-kpi-value">{fmt(summary.totalIncome)}</span><small>नोंदवलेली एकूण आवक</small></div></div>
        <div className="fin-kpi expense"><div className="fin-kpi-icon">↘</div><div className="fin-kpi-body"><span className="fin-kpi-label">एकूण खर्च</span><span className="fin-kpi-value">{fmt(summary.totalExpense)}</span><small>उत्पन्नाच्या {expenseRate}%</small></div></div>
        <div className="fin-kpi balance"><div className="fin-kpi-icon">₹</div><div className="fin-kpi-body"><span className="fin-kpi-label">निव्वळ शिल्लक</span><span className="fin-kpi-value">{fmt(summary.netBalance)}</span><small>सध्याची उपलब्ध शिल्लक</small></div></div>
        <div className="fin-kpi bills"><div className="fin-kpi-icon">▤</div><div className="fin-kpi-body"><span className="fin-kpi-label">प्रलंबित बिले</span><span className="fin-kpi-value">{summary.pendingBills || 0}</span><small>{fmt(summary.totalBillsAmount)} देय</small></div></div>
        <div className="fin-kpi funds"><div className="fin-kpi-icon">⌂</div><div className="fin-kpi-body"><span className="fin-kpi-label">प्राप्त निधी</span><span className="fin-kpi-value">{fmt(summary.fundsReceived)}</span><small>{budgetRate}% सरासरी अंदाजपत्रक वापर</small></div></div>
      </div>

      {/* Category Breakdown */}
      <div className="fin-grid-2">
        <div className="fin-card">
          <h3 className="fin-card-title">📈 वर्गनिहाय उत्पन्न</h3>
          {incCat.length > 0 ? (
            <div className="fin-breakdown-list">
              {incCat.sort((a, b) => b.total - a.total).map(c => {
                const pct = summary.totalIncome > 0 ? (Number(c.total) / summary.totalIncome * 100).toFixed(1) : 0;
                return (
                  <div key={c.category} className="fin-breakdown-item">
                    <span className="fin-breakdown-label">{cap(c.category)}</span>
                    <div className="fin-breakdown-bar"><div className="fin-breakdown-fill income" style={{ width: `${pct}%` }} /></div>
                    <span className="fin-breakdown-amt">{fmt(c.total)}</span>
                    <span className="fin-breakdown-pct">{pct}%</span>
                  </div>
                );
              })}
            </div>
          ) : <div className="fin-empty">अद्याप उत्पन्न नोंदवलेले नाही</div>}
        </div>
        <div className="fin-card">
          <h3 className="fin-card-title">📉 वर्गनिहाय खर्च</h3>
          {expCat.length > 0 ? (
            <div className="fin-breakdown-list">
              {expCat.sort((a, b) => b.total - a.total).map(c => {
                const pct = summary.totalExpense > 0 ? (Number(c.total) / summary.totalExpense * 100).toFixed(1) : 0;
                return (
                  <div key={c.category} className="fin-breakdown-item">
                    <span className="fin-breakdown-label">{cap(c.category)}</span>
                    <div className="fin-breakdown-bar"><div className="fin-breakdown-fill expense" style={{ width: `${pct}%` }} /></div>
                    <span className="fin-breakdown-amt">{fmt(c.total)}</span>
                    <span className="fin-breakdown-pct">{pct}%</span>
                  </div>
                );
              })}
            </div>
          ) : <div className="fin-empty">अद्याप खर्च नोंदवलेला नाही</div>}
        </div>
      </div>

      {/* Budget Utilization Overview */}
      {budgets.length > 0 && (
        <div className="fin-card">
          <h3 className="fin-card-title">📊 अंदाजपत्रक वापर</h3>
          <div className="fin-budget-bars">
            {budgets.slice(0, 10).map(b => (
              <div key={b.id} className="fin-budget-bar-row">
                <span className="fin-budget-bar-label">{cap(b.category)} ({b.financial_year})</span>
                <div className="fin-budget-bar-track">
                  <div className="fin-budget-bar-fill" style={{ width: `${Math.min(b.utilization_pct || 0, 100)}%`, background: (b.utilization_pct || 0) > 90 ? '#e74c3c' : (b.utilization_pct || 0) > 70 ? '#f39c12' : '#27ae60' }} />
                </div>
                <span className="fin-budget-bar-pct">{b.utilization_pct || 0}%</span>
                <span className="fin-budget-bar-amt">{fmt(b.actual_spent)} / {fmt(b.allocated_amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TRANSACTION TAB (shared for Income & Expense)
   ═══════════════════════════════════════════════════════════ */
function TransactionTab({
  title, type, colorClass, transactions, pagination,
  txSearch, setTxSearch, txPage, setTxPage,
  canCreate, canUpdate, canDelete, canApprove, canExport,
  showForm, formData, editId,
  onAdd, onEdit, onDelete, onApprove, onReverse, onExport, onSubmit, set, closeForm,
  categories, budgets, filterType, filters, setters,
}) {
  const [sort, setSort] = useState({ key: 'transaction_date', direction: 'desc' });
  const filtered = transactions.filter(t => t.type === filterType);
  const sorted = useMemo(() => sortRows(filtered, sort), [filtered, sort]);
  const isOpen = showForm === type;
  const canAnyAction = canUpdate || canDelete || canApprove;

  return (
    <div className="fin-section">
      <div className="fin-section-header">
        <h2>{title}</h2>
        <div className="fin-header-actions">
          {canExport && <button className="btn btn-outline" onClick={onExport}>Export</button>}
          {canCreate && <button className="btn btn-primary" onClick={onAdd}>+ {cap(type)} जोडा</button>}
        </div>
      </div>

      {/* Form */}
      {isOpen && (
        <div className="fin-card fin-form-card">
          <h3>{editId ? 'संपादन' : 'नवीन'} {cap(type)}</h3>
          <form onSubmit={onSubmit}>
            <div className="fin-form-grid">
              <div className="form-group">
                <label>वर्ग *</label>
                <select value={formData.category || ''} onChange={e => set('category', e.target.value)} required>
                  {categories.map(c => <option key={c} value={c}>{cap(c)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>रक्कम (₹) *</label>
                <input type="number" step="0.01" min="0.01" value={formData.amount || ''} onChange={e => set('amount', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>तारीख *</label>
                <input type="date" value={formData.transaction_date || ''} onChange={e => set('transaction_date', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>देयक पद्धत</label>
                <select value={formData.payment_mode || ''} onChange={e => set('payment_mode', e.target.value)}>
                  <option value="">-- निवडा --</option>
                  {PAYMENT_MODES.map(m => <option key={m} value={m}>{cap(m)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>संदर्भ / पावती क्र.</label>
                <input value={formData.reference_number || ''} onChange={e => set('reference_number', e.target.value)} placeholder="चेक/डीडी/यूपीआय संदर्भ" />
              </div>
              <div className="form-group">
                <label>पावती क्रमांक</label>
                <input value={formData.receipt_number || ''} onChange={e => set('receipt_number', e.target.value)} />
              </div>
              {type === 'expense' && (
                <>
                  <div className="form-group">
                    <label>विक्रेता / लाभार्थी</label>
                    <input value={formData.vendor_name || ''} onChange={e => set('vendor_name', e.target.value)} />
                  </div>
                  {budgets && budgets.length > 0 && (
                    <div className="form-group">
                      <label>अंदाजपत्रकाशी जोडा</label>
                      <select value={formData.budget_id || ''} onChange={e => set('budget_id', e.target.value || null)}>
                        <option value="">-- काहीही नाही --</option>
                        {budgets.map(b => <option key={b.id} value={b.id}>{cap(b.category)} ({b.financial_year})</option>)}
                      </select>
                    </div>
                  )}
                  {canApprove && (
                    <div className="form-group">
                      <label>Budget Override Reason</label>
                      <input value={formData.budget_override_reason || ''} onChange={e => set('budget_override_reason', e.target.value)} />
                    </div>
                  )}
                </>
              )}
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>वर्णन</label>
                <textarea rows={2} value={formData.description || ''} onChange={e => set('description', e.target.value)} />
              </div>
            </div>
            <div className="fin-form-actions">
              <button type="submit" className="btn btn-primary">{editId ? 'अद्ययावत करा' : 'जतन करा'}</button>
              <button type="button" className="btn btn-outline" onClick={closeForm}>रद्द करा</button>
            </div>
          </form>
        </div>
      )}

      {!isOpen && (
      <div className="fin-card">
        <div className="table-search-bar">
          <input placeholder="व्यवहार शोधा..." value={txSearch} onChange={e => { setTxSearch(e.target.value); setTxPage(1); }} />
          <select value={filters.txCategory} onChange={e => { setters.setTxCategory(e.target.value); setters.setTxPage(1); }}>
            <option value="">सर्व वर्ग</option>
            {categories.map(c => <option key={c} value={c}>{cap(c)}</option>)}
          </select>
          <select value={filters.txStatus} onChange={e => { setters.setTxStatus(e.target.value); setters.setTxPage(1); }}>
            <option value="">सर्व स्थिती</option>
            {['pending', 'approved', 'rejected', 'cancelled'].map(s => <option key={s} value={s}>{cap(s)}</option>)}
          </select>
          <select value={filters.txPaymentMode} onChange={e => { setters.setTxPaymentMode(e.target.value); setters.setTxPage(1); }}>
            <option value="">सर्व पद्धती</option>
            {PAYMENT_MODES.map(m => <option key={m} value={m}>{cap(m)}</option>)}
          </select>
          <input placeholder="2025-26" value={filters.txFinancialYear} onChange={e => { setters.setTxFinancialYear(e.target.value); setters.setTxPage(1); }} />
          <input type="date" max={filters.txToDate || undefined} value={filters.txFromDate} onChange={e => { setters.setTxFromDate(e.target.value); setters.setTxPage(1); }} />
          <input type="date" min={filters.txFromDate || undefined} value={filters.txToDate} onChange={e => { setters.setTxToDate(e.target.value); setters.setTxPage(1); }} />
        </div>

        <div className="fin-table-meta"><span><strong>{filtered.length}</strong> records</span></div>
        <div className="table-scroll finance-table-scroll">
          <table className="data-table finance-data-table">
            <thead>
              <tr><th><SortHeader sort={sort} setSort={setSort} column="financial_year">Year</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="category">Category</SortHeader></th><th>Sub-category</th><th><SortHeader sort={sort} setSort={setSort} column="allocated_amount">Allocated</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="actual_spent">Spent</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="remaining">Balance</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="utilization_pct">Use</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="status">Status</SortHeader></th>{canAnyAction && <th>Action</th>}</tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={canAnyAction ? 9 : 8} className="fin-empty">{cap(type)} नोंदी आढळल्या नाहीत</td></tr>
              ) : sorted.map(t => (
                <tr key={t.id}>
                  <td>{new Date(t.transaction_date).toLocaleDateString('mr-IN')}</td>
                  <td>{cap(t.category)}</td>
                  <td style={{ fontWeight: 600, color: type === 'income' ? '#27ae60' : '#e74c3c' }}>{fmt(t.amount)}</td>
                  <td>{cap(t.payment_mode) || '-'}</td>
                  <td><span className={`badge badge-${t.status === 'approved' ? 'success' : t.status === 'cancelled' || t.status === 'rejected' ? 'danger' : 'warning'}`}>{cap(t.status)}</span></td>
                  <td>{t.reference_number || t.receipt_number || '-'}</td>
                  <td>{t.description || '-'}</td>
                  <td>{t.recorder?.full_name || t.recorder?.username || '-'}</td>
                  {canAnyAction && (
                    <td>
                      {t.status !== 'cancelled' ? (
                        <>
                          {canApprove && t.status === 'pending' && <button className="btn-sm btn-primary" onClick={() => onApprove(t.id)}>✓</button>}{' '}
                          {canApprove && t.status === 'approved' && Number(t.amount || 0) > 0 && <button className="btn-sm btn-outline" onClick={() => onReverse(t.id)} title="Reverse">Reverse</button>}{' '}
                          {canUpdate && t.status !== 'approved' && <button className="btn-sm btn-outline" onClick={() => onEdit(t)}>✏️</button>}{' '}
                          {canDelete && t.status !== 'approved' && <button className="btn-sm btn-danger" onClick={() => onDelete(t.id)}>🗑️</button>}
                        </>
                      ) : '-'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="table-pagination">
            <button disabled={txPage <= 1} onClick={() => setTxPage(p => p - 1)}>← मागील</button>
            <span>पृष्ठ {txPage} / {pagination.totalPages}</span>
            <button disabled={txPage >= pagination.totalPages} onClick={() => setTxPage(p => p + 1)}>पुढील →</button>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   BUDGET TAB
   ═══════════════════════════════════════════════════════════ */
function BudgetTab({
  budgets, budgetYears, budgetYear, setBudgetYear,
  totalAllocated, totalSpent,
  canCreate, canUpdate, canDelete, canApprove,
  showForm, formData, editId,
  onAdd, onEdit, onDelete, onApprove, onSubmit, set, closeForm,
}) {
  const [sort, setSort] = useState({ key: 'financial_year', direction: 'desc' });
  const sortedBudgets = useMemo(() => sortRows(budgets, sort), [budgets, sort]);
  const isOpen = showForm === 'budget';
  const canAnyAction = canUpdate || canDelete || canApprove;

  return (
    <div className="fin-section">
      <div className="fin-section-header">
        <h2>📋 अंदाजपत्रक</h2>
        {canCreate && <button className="btn btn-primary" onClick={onAdd}>+ अंदाजपत्रक जोडा</button>}
      </div>

      {/* Summary bar */}
      <div className="fin-kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="fin-kpi balance"><div className="fin-kpi-icon">📋</div><div className="fin-kpi-body"><span className="fin-kpi-value">{budgets.length}</span><span className="fin-kpi-label">एकूण अंदाजपत्रके</span></div></div>
        <div className="fin-kpi income"><div className="fin-kpi-icon">💰</div><div className="fin-kpi-body"><span className="fin-kpi-value">{fmt(totalAllocated)}</span><span className="fin-kpi-label">एकूण वाटप</span></div></div>
        <div className="fin-kpi expense"><div className="fin-kpi-icon">📊</div><div className="fin-kpi-body"><span className="fin-kpi-value">{fmt(totalSpent)}</span><span className="fin-kpi-label">एकूण खर्च</span></div></div>
      </div>

      {isOpen && (
        <div className="fin-card fin-form-card">
          <h3>{editId ? 'संपादन' : 'नवीन'} अंदाजपत्रक</h3>
          <form onSubmit={onSubmit}>
            <div className="fin-form-grid">
              <div className="form-group">
                <label>आर्थिक वर्ष *</label>
                <input value={formData.financial_year || ''} onChange={e => set('financial_year', e.target.value)} placeholder="2025-26" required />
              </div>
              <div className="form-group">
                <label>वर्ग *</label>
                <select value={formData.category || ''} onChange={e => set('category', e.target.value)} required>
                  {BUDGET_CATS.map(c => <option key={c} value={c}>{cap(c)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>उप-वर्ग</label>
                <input value={formData.sub_category || ''} onChange={e => set('sub_category', e.target.value)} />
              </div>
              <div className="form-group">
                <label>वाटप रक्कम (₹) *</label>
                <input type="number" step="0.01" min="0" value={formData.allocated_amount || ''} onChange={e => set('allocated_amount', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>स्थिती</label>
                <select value={formData.status || 'draft'} onChange={e => set('status', e.target.value)}>
                  {['draft', 'proposed', 'approved', 'active', 'closed'].map(s => <option key={s} value={s}>{cap(s)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>टिपा</label>
                <input value={formData.notes || ''} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
            <div className="fin-form-actions">
              <button type="submit" className="btn btn-primary">{editId ? 'अद्ययावत करा' : 'जतन करा'}</button>
              <button type="button" className="btn btn-outline" onClick={closeForm}>रद्द करा</button>
            </div>
          </form>
        </div>
      )}

      {!isOpen && (
      <div className="fin-card">
        <div className="table-search-bar">
          <select value={budgetYear} onChange={e => setBudgetYear(e.target.value)}>
            <option value="">सर्व वर्षे</option>
            {budgetYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <div className="fin-table-meta"><span><strong>{budgets.length}</strong> records</span></div>
        <div className="table-scroll finance-table-scroll">
          <table className="data-table finance-data-table">
            <thead>
              <tr><th><SortHeader sort={sort} setSort={setSort} column="financial_year">Year</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="category">Category</SortHeader></th><th>Sub-category</th><th><SortHeader sort={sort} setSort={setSort} column="allocated_amount">Allocated</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="actual_spent">Spent</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="remaining">Balance</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="utilization_pct">Use</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="status">Status</SortHeader></th>{canAnyAction && <th>Action</th>}</tr>
            </thead>
            <tbody>
              {budgets.length === 0 ? (
                <tr><td colSpan={canAnyAction ? 9 : 8} className="fin-empty">अंदाजपत्रके आढळली नाहीत</td></tr>
               ) : sortedBudgets.map(b => (
                <tr key={b.id}>
                  <td>{b.financial_year}</td>
                  <td>{cap(b.category)}</td>
                  <td>{b.sub_category || '-'}</td>
                  <td style={{ fontWeight: 600 }}>{fmt(b.allocated_amount)}</td>
                  <td>{fmt(b.actual_spent)}</td>
                  <td style={{ color: b.remaining < 0 ? '#e74c3c' : '#27ae60' }}>{fmt(b.remaining)}</td>
                  <td>
                    <div className="fin-util-bar">
                      <div className="fin-util-fill" style={{ width: `${Math.min(b.utilization_pct, 100)}%`, background: b.utilization_pct > 90 ? '#e74c3c' : b.utilization_pct > 70 ? '#f39c12' : '#27ae60' }} />
                    </div>
                    <span style={{ fontSize: 11 }}>{b.utilization_pct}%</span>
                  </td>
                  <td><span className={`badge badge-${b.status === 'active' || b.status === 'approved' ? 'success' : b.status === 'closed' ? 'secondary' : 'warning'}`}>{cap(b.status)}</span></td>
                  {canAnyAction && (
                    <td>
                      {canApprove && !['approved', 'active', 'closed'].includes(b.status) && <button className="btn-sm btn-primary" onClick={() => onApprove(b.id)}>✓</button>}{' '}
                      {canUpdate && <button className="btn-sm btn-outline" onClick={() => onEdit(b)}>✏️</button>}{' '}
                      {canDelete && <button className="btn-sm btn-danger" onClick={() => onDelete(b.id)}>🗑️</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   BILLS TAB
   ═══════════════════════════════════════════════════════════ */
function BillTab({
  bills, pagination, billStatus, setBillStatus,
  billSearch, setBillSearch, billPage, setBillPage,
  canCreate, canUpdate, canDelete, canPay, canApprove,
  showForm, formData, editId,
  onAdd, onEdit, onDelete, onPay, onApprove, onSubmit, set, closeForm,
  budgets,
}) {
  const [sort, setSort] = useState({ key: 'due_date', direction: 'asc' });
  const sortedBills = useMemo(() => sortRows(bills, sort), [bills, sort]);
  const isOpen = showForm === 'bill';
  const isPay = showForm === 'pay_bill';
  const formOpen = isOpen || isPay;
  const canAnyAction = canUpdate || canDelete || canPay || canApprove;

  return (
    <div className="fin-section">
      <div className="fin-section-header">
        <h2>🧾 बिले व पावत्या</h2>
        {canCreate && <button className="btn btn-primary" onClick={onAdd}>+ नवीन बिल</button>}
      </div>

      {/* Bill Form */}
      {isOpen && (
        <div className="fin-card fin-form-card">
          <h3>{editId ? 'संपादन' : 'नवीन'} बिल</h3>
          <form onSubmit={onSubmit}>
            <div className="fin-form-grid">
              <div className="form-group">
                <label>बिल प्रकार *</label>
                <select value={formData.bill_type || ''} onChange={e => set('bill_type', e.target.value)} required>
                  {BILL_TYPES.map(t => <option key={t} value={t}>{cap(t)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Expense Category *</label>
                <select value={formData.expense_category || 'other_expense'} onChange={e => set('expense_category', e.target.value)} required>
                  {EXPENSE_CATS.map(c => <option key={c} value={c}>{cap(c)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>बिल क्रमांक</label>
                <input value={formData.bill_number || ''} onChange={e => set('bill_number', e.target.value)} placeholder="रिक्त असल्यास स्वयंनिर्मित" />
              </div>
              <div className="form-group">
                <label>विक्रेता नाव *</label>
                <input value={formData.vendor_name || ''} onChange={e => set('vendor_name', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>विक्रेता संपर्क</label>
                <input value={formData.vendor_contact || ''} onChange={e => set('vendor_contact', e.target.value)} />
              </div>
              <div className="form-group">
                <label>रक्कम (₹) *</label>
                <input type="number" step="0.01" min="0" value={formData.amount || ''} onChange={e => set('amount', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>कर रक्कम (₹)</label>
                <input type="number" step="0.01" min="0" value={formData.tax_amount || '0'} onChange={e => set('tax_amount', e.target.value)} />
              </div>
              <div className="form-group">
                <label>बिल तारीख *</label>
                <input type="date" max={formData.due_date || undefined} value={formData.bill_date || ''} onChange={e => set('bill_date', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>देय तारीख</label>
                <input type="date" min={formData.bill_date || undefined} value={formData.due_date || ''} onChange={e => set('due_date', e.target.value)} />
              </div>
              {budgets && budgets.length > 0 && (
                <div className="form-group">
                  <label>अंदाजपत्रकाशी जोडा</label>
                  <select value={formData.budget_id || ''} onChange={e => set('budget_id', e.target.value || null)}>
                    <option value="">-- काहीही नाही --</option>
                    {budgets.map(b => <option key={b.id} value={b.id}>{cap(b.category)} ({b.financial_year})</option>)}
                  </select>
                </div>
              )}
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>वर्णन</label>
                <textarea rows={2} value={formData.description || ''} onChange={e => set('description', e.target.value)} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Invoice / Bill Document URL</label>
                <input value={formData.invoice_document_url || ''} onChange={e => set('invoice_document_url', e.target.value)} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>टिपा</label>
                <input value={formData.notes || ''} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
            <div className="fin-form-actions">
              <button type="submit" className="btn btn-primary">{editId ? 'अद्ययावत करा' : 'बिल तयार करा'}</button>
              <button type="button" className="btn btn-outline" onClick={closeForm}>रद्द करा</button>
            </div>
          </form>
        </div>
      )}

      {/* Pay Bill Form */}
      {isPay && (
        <div className="fin-card fin-form-card">
          <h3>💳 देयक नोंदवा</h3>
          <form onSubmit={onSubmit}>
            <div className="fin-form-grid">
              <div className="form-group">
                <label>देयक रक्कम (₹) *</label>
                <input type="number" step="0.01" min="0.01" max={formData.max_amount || undefined} value={formData.amount || ''} onChange={e => set('amount', e.target.value)} required />
                {formData.max_amount !== undefined && <small>Outstanding: {fmt(formData.max_amount)}</small>}
              </div>
              <div className="form-group">
                <label>देयक पद्धत</label>
                <select value={formData.payment_mode || ''} onChange={e => set('payment_mode', e.target.value)}>
                  {PAYMENT_MODES.map(m => <option key={m} value={m}>{cap(m)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>देयक संदर्भ</label>
                <input value={formData.payment_reference || ''} onChange={e => set('payment_reference', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Payment Proof URL</label>
                <input value={formData.payment_proof_url || ''} onChange={e => set('payment_proof_url', e.target.value)} />
              </div>
              <div className="form-group">
                <label>तारीख</label>
                <input type="date" value={formData.transaction_date || ''} onChange={e => set('transaction_date', e.target.value)} />
              </div>
              {canApprove && (
                <div className="form-group">
                  <label>Budget Override Reason</label>
                  <input value={formData.budget_override_reason || ''} onChange={e => set('budget_override_reason', e.target.value)} />
                </div>
              )}
            </div>
            <div className="fin-form-actions">
              <button type="submit" className="btn btn-primary">देयक नोंदवा</button>
              <button type="button" className="btn btn-outline" onClick={closeForm}>रद्द करा</button>
            </div>
          </form>
        </div>
      )}

      {!formOpen && (
      <div className="fin-card">
        <div className="table-search-bar">
          <input placeholder="बिले शोधा..." value={billSearch} onChange={e => { setBillSearch(e.target.value); setBillPage(1); }} />
          <select value={billStatus} onChange={e => { setBillStatus(e.target.value); setBillPage(1); }}>
            <option value="">सर्व स्थिती</option>
            {['pending', 'partially_paid', 'paid', 'overdue', 'cancelled'].map(s => <option key={s} value={s}>{cap(s)}</option>)}
          </select>
        </div>

        <div className="fin-table-meta"><span><strong>{bills.length}</strong> records</span></div>
        <div className="table-scroll finance-table-scroll">
          <table className="data-table finance-data-table finance-bills-table">
            <thead>
              <tr><th><SortHeader sort={sort} setSort={setSort} column="bill_number">Bill No.</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="bill_type">Type</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="vendor_name">Vendor</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="amount">Amount</SortHeader></th><th>Tax</th><th><SortHeader sort={sort} setSort={setSort} column="total_amount">Total</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="paid_amount">Paid</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="due_date">Due Date</SortHeader></th><th>Proof</th><th><SortHeader sort={sort} setSort={setSort} column="approval_status">Approval</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="status">Status</SortHeader></th>{canAnyAction && <th>Action</th>}</tr>
            </thead>
            <tbody>
              {bills.length === 0 ? (
                <tr><td colSpan={canAnyAction ? 12 : 11} className="fin-empty">बिले आढळली नाहीत</td></tr>
               ) : sortedBills.map(b => {
                const isOverdue = b.status !== 'paid' && b.status !== 'cancelled' && b.due_date && new Date(b.due_date) < new Date();
                const billOutstanding = outstanding(b);
                return (
                  <tr key={b.id} className={isOverdue ? 'fin-row-overdue' : ''}>
                    <td style={{ fontWeight: 600 }}>{b.bill_number}</td>
                    <td>{cap(b.bill_type)}</td>
                    <td>{b.vendor_name}</td>
                    <td>{fmt(b.amount)}</td>
                    <td>{fmt(b.tax_amount)}</td>
                    <td style={{ fontWeight: 600 }}>{fmt(b.total_amount)}</td>
                    <td style={{ color: Number(b.paid_amount) >= Number(b.total_amount) ? '#27ae60' : '#e74c3c' }}>{fmt(b.paid_amount)}</td>
                    <td>{b.due_date ? new Date(b.due_date).toLocaleDateString('mr-IN') : '-'}</td>
                    <td>
                      {b.invoice_document_url ? <a href={b.invoice_document_url} target="_blank" rel="noreferrer">Invoice</a> : '-'}
                      {b.payment_proof_url ? <><br /><a href={b.payment_proof_url} target="_blank" rel="noreferrer">Payment</a></> : null}
                    </td>
                    <td><span className={`badge badge-${b.approval_status === 'approved' ? 'success' : b.approval_status === 'rejected' ? 'danger' : 'warning'}`}>{cap(b.approval_status || 'pending')}</span></td>
                    <td>
                      <span className={`badge badge-${b.status === 'paid' ? 'success' : b.status === 'overdue' || b.status === 'cancelled' ? 'danger' : 'warning'}`}>
                        {cap(b.status)}
                      </span>
                    </td>
                    {canAnyAction && (
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {canApprove && b.approval_status !== 'approved' && b.status !== 'cancelled' && (
                          <button className="btn-sm btn-primary" onClick={() => onApprove(b.id)} title="मंजूर करा">✓</button>
                        )}{' '}
                        {canPay && b.approval_status === 'approved' && b.status !== 'paid' && b.status !== 'cancelled' && billOutstanding > 0 && (
                          <button className="btn-sm btn-primary" onClick={() => onPay(b)} title="देयक नोंदवा">💳</button>
                        )}{' '}
                        {canUpdate && <button className="btn-sm btn-outline" onClick={() => onEdit(b)}>✏️</button>}{' '}
                        {canDelete && <button className="btn-sm btn-danger" onClick={() => onDelete(b.id)}>🗑️</button>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="table-pagination">
            <button disabled={billPage <= 1} onClick={() => setBillPage(p => p - 1)}>← मागील</button>
            <span>पृष्ठ {billPage} / {pagination.totalPages}</span>
            <button disabled={billPage >= pagination.totalPages} onClick={() => setBillPage(p => p + 1)}>पुढील →</button>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   FUND ALLOCATION TAB
   ═══════════════════════════════════════════════════════════ */
function FundTab({
  funds, fundYears, fundYear, setFundYear,
  totalFundSanctioned, totalFundReceived, totalFundUtilized,
  canCreate, canUpdate, canDelete,
  showForm, formData, editId,
  onAdd, onEdit, onDelete, onSubmit, set, closeForm,
}) {
  const [sort, setSort] = useState({ key: 'financial_year', direction: 'desc' });
  const sortedFunds = useMemo(() => sortRows(funds, sort), [funds, sort]);
  const isOpen = showForm === 'fund';
  const canAnyAction = canUpdate || canDelete;

  return (
    <div className="fin-section">
      <div className="fin-section-header">
        <h2>🏦 निधी वाटप व अनुदान</h2>
        {canCreate && <button className="btn btn-primary" onClick={onAdd}>+ निधी जोडा</button>}
      </div>

      <div className="fin-kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="fin-kpi income"><div className="fin-kpi-icon">📝</div><div className="fin-kpi-body"><span className="fin-kpi-value">{fmt(totalFundSanctioned)}</span><span className="fin-kpi-label">एकूण मंजूर</span></div></div>
        <div className="fin-kpi balance"><div className="fin-kpi-icon">✅</div><div className="fin-kpi-body"><span className="fin-kpi-value">{fmt(totalFundReceived)}</span><span className="fin-kpi-label">एकूण प्राप्त</span></div></div>
        <div className="fin-kpi expense"><div className="fin-kpi-icon">📊</div><div className="fin-kpi-body"><span className="fin-kpi-value">{fmt(totalFundUtilized)}</span><span className="fin-kpi-label">एकूण वापर</span></div></div>
      </div>

      {isOpen && (
        <div className="fin-card fin-form-card">
          <h3>{editId ? 'संपादन' : 'नवीन'} निधी वाटप</h3>
          <form onSubmit={onSubmit}>
            <div className="fin-form-grid">
              <div className="form-group">
                <label>निधी नाव *</label>
                <input value={formData.fund_name || ''} onChange={e => set('fund_name', e.target.value)} placeholder="उदा. १४ वा वित्त आयोग" required />
              </div>
              <div className="form-group">
                <label>स्रोत *</label>
                <select value={formData.source || ''} onChange={e => set('source', e.target.value)} required>
                  {FUND_SOURCES.map(s => <option key={s} value={s}>{cap(s)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>योजना नाव</label>
                <input value={formData.scheme_name || ''} onChange={e => set('scheme_name', e.target.value)} placeholder="मनरेगा, पीएमएवाय, इ." />
              </div>
              <div className="form-group">
                <label>आर्थिक वर्ष *</label>
                <input value={formData.financial_year || ''} onChange={e => set('financial_year', e.target.value)} placeholder="2025-26" required />
              </div>
              <div className="form-group">
                <label>मंजूर रक्कम (₹) *</label>
                <input type="number" step="0.01" min="0" value={formData.sanctioned_amount || ''} onChange={e => set('sanctioned_amount', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>प्राप्त रक्कम (₹)</label>
                <input type="number" step="0.01" min="0" value={formData.received_amount || ''} onChange={e => set('received_amount', e.target.value)} />
              </div>
              <div className="form-group">
                <label>वापरलेली रक्कम (₹)</label>
                <input type="number" step="0.01" min="0" value={formData.utilized_amount || ''} onChange={e => set('utilized_amount', e.target.value)} />
              </div>
              <div className="form-group">
                <label>स्थिती</label>
                <select value={formData.status || 'sanctioned'} onChange={e => set('status', e.target.value)}>
                  {['sanctioned', 'partially_received', 'fully_received', 'utilized', 'refunded', 'expired'].map(s => <option key={s} value={s}>{cap(s)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>मंजुरी आदेश क्र.</label>
                <input value={formData.sanction_order_number || ''} onChange={e => set('sanction_order_number', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Sanction Document URL</label>
                <input value={formData.sanction_document_url || ''} onChange={e => set('sanction_document_url', e.target.value)} />
              </div>
              <div className="form-group">
                <label>मंजुरी तारीख</label>
                <input type="date" max={formData.release_date || formData.utilization_deadline || undefined} value={formData.sanction_date || ''} onChange={e => set('sanction_date', e.target.value)} />
              </div>
              <div className="form-group">
                <label>वितरण तारीख</label>
                <input type="date" min={formData.sanction_date || undefined} max={formData.utilization_deadline || undefined} value={formData.release_date || ''} onChange={e => set('release_date', e.target.value)} />
              </div>
              <div className="form-group">
                <label>वापर अंतिम तारीख</label>
                <input type="date" min={formData.release_date || formData.sanction_date || undefined} value={formData.utilization_deadline || ''} onChange={e => set('utilization_deadline', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Utilization Certificate URL</label>
                <input value={formData.utilization_certificate_url || ''} onChange={e => set('utilization_certificate_url', e.target.value)} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>उद्देश</label>
                <textarea rows={2} value={formData.purpose || ''} onChange={e => set('purpose', e.target.value)} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>टिपा</label>
                <input value={formData.notes || ''} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
            <div className="fin-form-actions">
              <button type="submit" className="btn btn-primary">{editId ? 'अद्ययावत करा' : 'जतन करा'}</button>
              <button type="button" className="btn btn-outline" onClick={closeForm}>रद्द करा</button>
            </div>
          </form>
        </div>
      )}

      {!isOpen && (
      <div className="fin-card">
        <div className="table-search-bar">
          <select value={fundYear} onChange={e => setFundYear(e.target.value)}>
            <option value="">सर्व वर्षे</option>
            {fundYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <div className="fin-table-meta"><span><strong>{funds.length}</strong> records</span></div>
        <div className="table-scroll finance-table-scroll">
          <table className="data-table finance-data-table">
            <thead>
              <tr><th><SortHeader sort={sort} setSort={setSort} column="fund_name">Fund</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="source">Source</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="scheme_name">Scheme</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="financial_year">Year</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="sanctioned_amount">Sanctioned</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="received_amount">Received</SortHeader></th><th><SortHeader sort={sort} setSort={setSort} column="utilized_amount">Utilized</SortHeader></th><th>Proof</th><th><SortHeader sort={sort} setSort={setSort} column="status">Status</SortHeader></th>{canAnyAction && <th>Action</th>}</tr>
            </thead>
            <tbody>
              {funds.length === 0 ? (
                <tr><td colSpan={canAnyAction ? 10 : 9} className="fin-empty">निधी वाटप आढळले नाहीत</td></tr>
              ) : sortedFunds.map(f => {
                const recPct = Number(f.sanctioned_amount) > 0 ? Math.round(Number(f.received_amount) / Number(f.sanctioned_amount) * 100) : 0;
                return (
                  <tr key={f.id}>
                    <td style={{ fontWeight: 600 }}>{f.fund_name}</td>
                    <td>{cap(f.source)}</td>
                    <td>{f.scheme_name || '-'}</td>
                    <td>{f.financial_year}</td>
                    <td>{fmt(f.sanctioned_amount)}</td>
                    <td>
                      {fmt(f.received_amount)}
                      <span style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>({recPct}%)</span>
                    </td>
                    <td>{fmt(f.utilized_amount)}</td>
                    <td>
                      {f.sanction_document_url ? <a href={f.sanction_document_url} target="_blank" rel="noreferrer">Sanction</a> : '-'}
                      {f.utilization_certificate_url ? <><br /><a href={f.utilization_certificate_url} target="_blank" rel="noreferrer">UC</a></> : null}
                    </td>
                    <td>
                      <span className={`badge badge-${f.status === 'utilized' || f.status === 'fully_received' ? 'success' : f.status === 'expired' || f.status === 'refunded' ? 'danger' : 'warning'}`}>
                        {cap(f.status)}
                      </span>
                    </td>
                    {canAnyAction && (
                      <td>
                        {canUpdate && <button className="btn-sm btn-outline" onClick={() => onEdit(f)}>✏️</button>}{' '}
                        {canDelete && <button className="btn-sm btn-danger" onClick={() => onDelete(f.id)}>🗑️</button>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CSS
   ═══════════════════════════════════════════════════════════ */
const finCSS = `
  .fin-page { display: flex; flex-direction: column; gap: 16px; }

  /* Header */
  .fin-header {
    background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 60%, #43a047 100%);
    border-radius: 12px; padding: 20px 24px; color: #fff;
    box-shadow: 0 4px 20px rgba(27,94,32,0.2);
  }
  .fin-title { margin: 0; font-size: 22px; font-weight: 700; }
  .fin-subtitle { margin: 4px 0 0; font-size: 13px; opacity: 0.8; }

  /* Tabs */
  .fin-tabs {
    display: flex; gap: 4px; background: #f5f5f5; border-radius: 10px; padding: 4px;
    overflow-x: auto;
  }
  .fin-tab {
    flex: 1; min-width: 0; padding: 10px 12px; border: none; background: none;
    border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
    color: #666; transition: all 0.2s; white-space: nowrap;
    display: flex; align-items: center; justify-content: center; gap: 6px;
  }
  .fin-tab:hover { background: #e8e8e8; color: #333; }
  .fin-tab.active { background: #fff; color: #1b5e20; font-weight: 700; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }
  .fin-tab-icon { font-size: 16px; }

  /* KPI Row */
  .fin-kpi-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
  .fin-kpi {
    background: #fff; border-radius: 10px; padding: 16px;
    display: flex; align-items: center; gap: 12px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
    border-left: 4px solid #ccc;
  }
  .fin-kpi.income { border-left-color: #27ae60; }
  .fin-kpi.expense { border-left-color: #e74c3c; }
  .fin-kpi.balance { border-left-color: #2196f3; }
  .fin-kpi.bills { border-left-color: #f39c12; }
  .fin-kpi.funds { border-left-color: #9b59b6; }
  .fin-kpi-icon { font-size: 24px; }
  .fin-kpi-body { display: flex; flex-direction: column; }
  .fin-kpi-value { font-size: 1.3rem; font-weight: 800; color: #1a1a2e; }
  .fin-kpi-label { font-size: 11px; color: #888; margin-top: 2px; }

  /* Section */
  .fin-section { display: flex; flex-direction: column; gap: 14px; }
  .fin-section-header { display: flex; align-items: center; justify-content: space-between; }
  .fin-section-header h2 { margin: 0; font-size: 18px; }
  .fin-header-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }

  /* Cards */
  .fin-card {
    background: #fff; border-radius: 10px; padding: 18px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  }
  .fin-card-title { margin: 0 0 14px; font-size: 15px; font-weight: 700; color: #333; }

  .fin-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

  /* Breakdown bars */
  .fin-breakdown-list { display: flex; flex-direction: column; gap: 8px; }
  .fin-breakdown-item { display: grid; grid-template-columns: 120px 1fr 80px 45px; align-items: center; gap: 8px; font-size: 13px; }
  .fin-breakdown-label { color: #555; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .fin-breakdown-bar { height: 8px; background: #f0f0f0; border-radius: 4px; overflow: hidden; }
  .fin-breakdown-fill { height: 100%; border-radius: 4px; transition: width 0.3s; }
  .fin-breakdown-fill.income { background: #66bb6a; }
  .fin-breakdown-fill.expense { background: #ef5350; }
  .fin-breakdown-amt { text-align: right; font-weight: 600; color: #333; font-size: 12px; }
  .fin-breakdown-pct { color: #aaa; font-size: 11px; }

  /* Budget utilization bars */
  .fin-budget-bars { display: flex; flex-direction: column; gap: 10px; }
  .fin-budget-bar-row { display: grid; grid-template-columns: 200px 1fr 50px 120px; align-items: center; gap: 10px; font-size: 13px; }
  .fin-budget-bar-label { color: #555; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .fin-budget-bar-track { height: 10px; background: #f0f0f0; border-radius: 5px; overflow: hidden; }
  .fin-budget-bar-fill { height: 100%; border-radius: 5px; transition: width 0.4s; }
  .fin-budget-bar-pct { font-weight: 700; font-size: 12px; text-align: right; }
  .fin-budget-bar-amt { font-size: 11px; color: #888; }

  /* Utilization bar in table */
  .fin-util-bar { height: 6px; width: 60px; background: #f0f0f0; border-radius: 3px; overflow: hidden; display: inline-block; margin-right: 4px; }
  .fin-util-fill { height: 100%; border-radius: 3px; }

  /* Form */
  .fin-form-card { border: 2px solid #e8f5e9; }
  .fin-form-card h3 { margin: 0 0 14px; font-size: 16px; color: #1b5e20; }
  .fin-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .fin-form-actions { display: flex; gap: 10px; margin-top: 14px; }

  /* Small buttons */
  .btn-sm { padding: 4px 8px; font-size: 12px; border-radius: 4px; cursor: pointer; border: 1px solid #ddd; background: #fff; }
  .btn-sm.btn-primary { background: #1b5e20; color: #fff; border-color: #1b5e20; }
  .btn-sm.btn-danger { background: #fff; color: #e74c3c; border-color: #e74c3c; }
  .btn-sm.btn-outline:hover { background: #f5f5f5; }

  .fin-empty { text-align: center; color: #ccc; padding: 24px; font-size: 14px; }

  .fin-row-overdue { background: #fff3e0 !important; }

  /* Badge */
  .badge-secondary { background: #e0e0e0; color: #555; }

  .fin-table-meta {
    display: flex;
    min-height: 34px;
    align-items: center;
    justify-content: space-between;
    padding: 8px 4px;
    color: #8290a1;
    font-size: 0.78rem;
  }
  .fin-table-meta strong { color: #215c8c; font-size: 0.95rem; }
  .finance-table-scroll {
    min-height: 340px;
    max-height: calc(100vh - 390px);
    border: 1px solid #e2e9f0;
    border-radius: 10px;
    scrollbar-gutter: stable;
  }
  .finance-data-table { min-width: 1080px; transition: opacity 0.12s ease; }
  .finance-bills-table { min-width: 1320px; }
  .finance-data-table th,
  .finance-data-table td {
    padding: 8px 13px !important;
    border-bottom: 1px solid #e8eef4 !important;
    color: #334a64;
    font-size: 0.78rem !important;
    vertical-align: middle;
  }
  .finance-data-table thead th {
    height: 38px;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    background: #f7fafe !important;
  }
  .finance-data-table tbody tr:nth-child(even) { background: #fbfdff; }
  .finance-data-table tbody tr:hover { background: #f2f8fd; }
  .fin-sort-header {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0;
    border: 0;
    background: none;
    color: #4a6079;
    font: 800 0.72rem inherit;
    cursor: pointer;
    white-space: nowrap;
  }
  .fin-sort-header span { color: #9aacbd; }
  .fin-sort-header.active,
  .fin-sort-header.active span { color: #106aaa; }

  /* Modern finance workspace */
  .fin-page { gap: 14px; --fin-navy:#143f68; --fin-blue:#176da8; --fin-border:#dce7f1; --fin-muted:#718196; }
  .fin-header {
    display:flex; align-items:center; justify-content:space-between; gap:24px;
    padding:20px 22px; color:var(--fin-navy); border:1px solid var(--fin-border); border-radius:18px;
    background:radial-gradient(circle at 88% 10%,rgba(33,139,201,.12),transparent 27%),linear-gradient(112deg,#fff 0%,#f8fbff 62%,#edf7ff 100%);
    box-shadow:0 12px 30px rgba(24,67,104,.07);
  }
  .fin-heading-block{display:flex;align-items:center;gap:14px}.fin-heading-icon{display:grid;width:48px;height:48px;flex:0 0 48px;place-items:center;border:1px solid #c8e0f1;border-radius:13px;background:linear-gradient(135deg,#eaf6ff,#fff);color:#176ca8;font-size:22px;font-weight:850;box-shadow:0 5px 14px rgba(28,104,157,.09)}
  .fin-eyebrow{display:block;margin-bottom:2px;color:#3281b6;font-size:9px;font-weight:800;letter-spacing:.08em;text-transform:uppercase}.fin-title{color:var(--fin-navy);font-size:20px;font-weight:800}.fin-subtitle{margin-top:4px;color:var(--fin-muted);font-size:11px;opacity:1}.fin-header-context{min-width:180px;padding:10px 13px;border:1px solid #d7e7f3;border-radius:11px;background:rgba(255,255,255,.75)}.fin-header-context span,.fin-header-context strong{display:block}.fin-header-context span{color:#8292a4;font-size:9px;font-weight:700}.fin-header-context strong{margin-top:3px;color:#244c70;font-size:12px}
  .fin-tabs{gap:5px;padding:5px;border:1px solid var(--fin-border);border-radius:13px;background:#fff;box-shadow:0 5px 16px rgba(30,65,98,.045)}.fin-tab{min-height:40px;color:#526a82;font-size:12px;font-weight:650}.fin-tab:hover{background:#f1f7fc;color:#195f94}.fin-tab.active{color:#17689f;background:#eaf5fd;box-shadow:none;font-weight:800}.fin-tab-icon{display:grid;width:24px;height:24px;place-items:center;border-radius:7px;background:#f2f5f8;font-size:13px}.fin-tab.active .fin-tab-icon{background:#fff}
  .fin-summary-toolbar{display:flex;align-items:flex-end;justify-content:space-between;gap:14px;padding:13px 15px;border:1px solid var(--fin-border);border-radius:13px;background:#fff}.fin-summary-toolbar>div{display:flex;align-items:center;gap:9px}.fin-summary-toolbar span{color:#718196;font-size:10px;font-weight:750}.fin-summary-toolbar select{height:38px;padding:0 30px 0 11px;border:1px solid #cfdeea;border-radius:8px;background:#fff;color:#294d6c;font:650 11px inherit}.fin-report-control{margin-left:auto}.fin-report-control .btn{min-height:38px;padding:7px 13px;border-width:1px;border-radius:8px;font-size:11px}
  .fin-kpi-row{gap:12px}.fin-kpi{position:relative;min-width:0;padding:15px;border:1px solid var(--fin-border);border-left-width:3px;border-radius:14px;box-shadow:0 8px 22px rgba(24,67,104,.055);overflow:hidden}.fin-kpi-icon{display:grid;width:39px;height:39px;flex:0 0 39px;place-items:center;border-radius:11px;background:#edf5fb;color:#2876ac;font-size:18px;font-style:normal;font-weight:850}.fin-kpi.income .fin-kpi-icon{background:#eaf8f1;color:#268859}.fin-kpi.expense .fin-kpi-icon{background:#fff0f1;color:#d14d57}.fin-kpi.bills .fin-kpi-icon{background:#fff5df;color:#c98210}.fin-kpi.funds .fin-kpi-icon{background:#f2effc;color:#7657b1}.fin-kpi-label{order:-1;margin:0 0 3px;color:#718196;font-size:10px;font-weight:750}.fin-kpi-value{color:#153e65;font-size:1.13rem;font-weight:820;white-space:nowrap}.fin-kpi-body small{margin-top:3px;color:#8a98a8;font-size:9px;font-weight:600;white-space:nowrap}
  .fin-card{padding:16px;border:1px solid var(--fin-border);border-radius:14px;box-shadow:0 8px 24px rgba(35,69,101,.055)}.fin-card-title{padding-bottom:11px;border-bottom:1px solid #edf2f6;color:var(--fin-navy);font-size:14px}.fin-breakdown-item{grid-template-columns:minmax(120px,1fr) minmax(100px,1.8fr) 95px 42px;padding:5px 0}.fin-breakdown-bar,.fin-budget-bar-track{background:#e9eff5}.fin-breakdown-fill.income{background:linear-gradient(90deg,#2fa66c,#67c48f)}.fin-breakdown-fill.expense{background:linear-gradient(90deg,#e15a62,#f18a8e)}.fin-budget-bar-row{padding:6px 0;border-bottom:1px solid #f0f3f6}.fin-budget-bar-row:last-child{border-bottom:0}
  .fin-form-card{border:1px solid #bdd8ea;background:linear-gradient(135deg,#fbfdff,#fff)}.fin-form-card h3{color:var(--fin-navy)}.fin-section-header h2{color:var(--fin-navy);font-size:17px}.fin-section-header>.btn-primary,.fin-header-actions>.btn-primary{color:#17689f!important;border-color:#4b8dbd!important;background:#fff!important;box-shadow:0 3px 10px rgba(31,103,154,.08)!important}
  .btn-sm.btn-primary{background:#176da8;color:#fff;border-color:#176da8}

  @media (max-width: 900px) {
    .fin-kpi-row { grid-template-columns: repeat(2, 1fr); }
    .fin-grid-2 { grid-template-columns: 1fr; }
    .fin-form-grid { grid-template-columns: 1fr; }
    .fin-breakdown-item { grid-template-columns: 100px 1fr 70px 40px; }
    .fin-budget-bar-row { grid-template-columns: 1fr; gap: 4px; }
    .fin-tabs { gap: 2px; }
    .fin-tab { font-size: 12px; padding: 8px 6px; }
    .fin-header-context{display:none}.fin-summary-toolbar{align-items:stretch;flex-direction:column}.fin-summary-toolbar>div{flex-wrap:wrap}.fin-report-control{margin-left:0}
  }
  @media (max-width: 600px) {
    .fin-kpi-row { grid-template-columns: 1fr; }
    .fin-section-header { flex-direction: column; gap: 8px; align-items: flex-start; }
    .fin-header-actions { width: 100%; }
  }
`;
